import { useBillStore } from "../store";
import { useCallback, useMemo, useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { roundAmount2Dig } from "utils/amount";
import { useFinancialYearStore } from "store";
import { useCreateShippingInfo, useUpdateShippingInfo } from "http-hooks/shippinginfo/useShippinginfo";
import { useCheckSalebillDuplicate, useCreateSaleBill, useUpdateSaleBill } from "http-hooks/salebill/useSalebill";
import { useAllAgOrderDetails } from "http-hooks/agorderdetail/useAgorderdetail";
import { useProfilingMasterByParty } from "http-hooks/profilingMaster/useProfilingMaster";
import { usePackingListDetails } from "http-hooks/salebill/useSalebill";
import useAuth from "hooks/useAuth";
import useCompanyPreferences from "hooks/useCompanyPreferences";
import { exclusiveToInclusive, getItemNetRate } from "utils/salebillRate";

export const useBill = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentFinancialYear } = useFinancialYearStore();
  const fys = currentFinancialYear?.fys;
  const fye = currentFinancialYear?.fye;

  // Get user account type for trader bill type validation
  const { agttp, companyStateId, companyHasGst } = useAuth();
  const { isTraderSalebillAutoNumberEnabled } = useCompanyPreferences();

  // Bill header state and actions
  const billHeader = useBillStore((state) => state.billHeader);
  const setBillHeader = useBillStore((state) => state.setBillHeader);
  const updateBillHeader = useBillStore((state) => state.updateBillHeader);
  const resetBillHeader = useBillStore((state) => state.resetBillHeader);
  const resetStore = useBillStore((state) => state.resetStore);
  const isHeaderComplete = useBillStore((state) => state.isHeaderComplete);

  // Document type state and actions
  const docType = useBillStore((state) => state.docType);
  const setDocType = useBillStore((state) => state.setDocType);

  // Select boxes state and actions
  const selectBoxes = useBillStore((state) => state.selectBoxes);
  const updateSelectBox = useBillStore((state) => state.updateSelectBox);
  const setSelectBoxes = useBillStore((state) => state.setSelectBoxes);
  const resetSelectBoxes = useBillStore((state) => state.resetSelectBoxes);

  // Table data state and actions
  const tableData = useBillStore((state) => state.tableData);
  const setTableData = useBillStore((state) => state.setTableData);
  const showTables = useBillStore((state) => state.showTables);
  const setShowTables = useBillStore((state) => state.setShowTables);
  const addItemsToTable = useBillStore((state) => state.addItemsToTable);
  const removeTableItem = useBillStore((state) => state.removeTableItem);
  const updateTableItem = useBillStore((state) => state.updateTableItem);

  // Filters and query state and actions
  const pricelistFilters = useBillStore((state) => state.pricelistFilters);
  const setPricelistFilters = useBillStore(
    (state) => state.setPricelistFilters
  );
  const queryPricelist = useBillStore((state) => state.queryPricelist);
  const setQueryPricelist = useBillStore((state) => state.setQueryPricelist);

  // Before tax discounts state and actions
  const beforeTaxDiscounts = useBillStore((state) => state.beforeTaxDiscounts);
  const setBeforeTaxDiscounts = useBillStore((state) => state.setBeforeTaxDiscounts);
  const updateBeforeTaxDiscount = useBillStore((state) => state.updateBeforeTaxDiscount);

  // After tax discounts state and actions
  const afterTaxDiscounts = useBillStore((state) => state.afterTaxDiscounts);
  const setAfterTaxDiscounts = useBillStore((state) => state.setAfterTaxDiscounts);
  const updateAfterTaxDiscount = useBillStore((state) => state.updateAfterTaxDiscount);

  // Edit mode state
  const editBillId = useBillStore((state) => state.editBillId);
  const savedBillDiscounts = useBillStore((state) => state.savedBillDiscounts);
  const savedAfterTaxDiscounts = useBillStore((state) => state.savedAfterTaxDiscounts);

  // Agent commission state and actions
  const agentCommission = useBillStore((state) => state.agentCommission);
  const setAgentCommission = useBillStore((state) => state.setAgentCommission);
  const updateAgentCommission = useBillStore((state) => state.updateAgentCommission);

  // Case diversion state and actions (for PL bills)
  const confirmpltransfer = useBillStore((state) => state.confirmpltransfer);
  const setCaseDiversion = useBillStore((state) => state.setCaseDiversion);
  const clearCaseDiversion = useBillStore((state) => state.clearCaseDiversion);

  // Modal states and actions
  const showCaseDiversionModal = useBillStore((state) => state.showCaseDiversionModal);
  const setShowCaseDiversionModal = useBillStore((state) => state.setShowCaseDiversionModal);
  const resetCaseDiversionModal = useBillStore((state) => state.resetCaseDiversionModal);

  // Handle case diversion confirmation
  const handleCaseDiversionConfirm = useCallback((newCustomer) => {
    setCaseDiversion(newCustomer);
    // Don't reset the modal here - we need transferCustomer for the API call
    // It will be reset when the bill is created or when user cancels
    toast.success("Case Diverted - The packing list will be transferred to the selected customer");
  }, [setCaseDiversion]);

  // F2 key handler for case diversion modal (only for PL bills)
  useEffect(() => {
    const handleKeyDown = (e) => {
      // F2 key (keyCode 113)
      if (e.keyCode === 113 && selectBoxes.billType?.value === "PL") {
        e.preventDefault();
        setShowCaseDiversionModal(true);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectBoxes.billType, setShowCaseDiversionModal]);

  // Bill mode state (Default/Without Items)
  const [billMode, setBillModeLocal] = useState({ value: "default", label: "Default" });
  
  const setBillMode = useCallback((mode) => {
    setBillModeLocal(mode);
    updateBillHeader({ billdefault: mode?.value === "without_items" ? 0 : 1 });
  }, [updateBillHeader]);

  // Sync local billMode from billHeader.billdefault (for edit mode population)
  useEffect(() => {
    const expected = billHeader.billdefault === 0
      ? { value: "without_items", label: "Without Items" }
      : { value: "default", label: "Default" };
    if (billMode.value !== expected.value) {
      setBillModeLocal(expected);
    }
  }, [billHeader.billdefault]); // eslint-disable-line react-hooks/exhaustive-deps

  // Agent bills always use G. Trader bills explicitly choose sale or purchase.
  const traderBillType = useMemo(() => {
    if (agttp === 0) {
      return "G";
    }

    const traderbilltype = searchParams.get("traderbilltyp");
    return ["P", "S"].includes(traderbilltype) ? traderbilltype : "S";
  }, [agttp, searchParams]);

  // GST is not charged on a sale to a foreign party. For purchases, retain the
  // existing tax flow because import/RCM treatment is a separate concern.
  const taxParty = useMemo(() => {
    if (agttp === 1 && traderBillType === "P") {
      return null;
    }

    return selectBoxes.customer;
  }, [agttp, selectBoxes.customer, traderBillType]);
  const isForeignCounterparty =
    Boolean(taxParty?.ps_country) &&
    taxParty.ps_country.toUpperCase() !== "IN";
  // Price lists are INR-only. A foreign invoice starts with blank FC line rates.
  const isForeignCurrencyBill = isForeignCounterparty;
  const hasApplicableGst = companyHasGst && !isForeignCounterparty;

  const setTraderBillType = useCallback(
    (traderbilltype) => {
      if (agttp !== 1 || !["P", "S"].includes(traderbilltype)) {
        return;
      }

      const params = new URLSearchParams(searchParams);
      params.set("traderbilltyp", traderbilltype);
      setSearchParams(params);
      updateSelectBox("customer", null);
      updateSelectBox("vendor", null);
      updateSelectBox("order", null);
      updateSelectBox("packingList", null);
      updateSelectBox("pricelist", null);
      updateBillHeader({ cusid: "", vendid: "", pldate: null });
      setTableData([]);
      setShowTables(false);
    },
    [
      agttp,
      searchParams,
      setSearchParams,
      updateSelectBox,
      updateBillHeader,
      setTableData,
      setShowTables,
    ]
  );

  // Other actions
  const populateBillFromData = useBillStore(
    (state) => state.populateBillFromData
  );

  // Shipping Info Create
  const { mutateAsync: createShippingInfo, isPending: isCreatingShippingInfo } = useCreateShippingInfo();

  // Shipping Info Update
  const { mutateAsync: updateShippingInfo, isPending: isUpdatingShippingInfo } = useUpdateShippingInfo();

  // Sale Bill Create
  const { mutateAsync: createSaleBill, isPending: isCreatingSaleBill } = useCreateSaleBill();

  // Sale Bill Update
  const { mutateAsync: updateSaleBill, isPending: isUpdatingSaleBill } = useUpdateSaleBill();

  // Salebill Duplicate Check
  const { mutateAsync: checkSalebillDuplicate } = useCheckSalebillDuplicate();

  // Order details fetching hook
  const orderDetailsFilters = useMemo(() => {
    const filters = {
      ignorePaginate: 'true',
      calculate_tax: 'true' // Request HSN-based tax calculation
    };
    
    // Handle single or multiple orders
    if (selectBoxes.order) {
      if (Array.isArray(selectBoxes.order)) {
        // Multiple orders - join with comma
        filters.orderno = selectBoxes.order.map(order => order.ordno).join(',');
      } else {
        // Single order
        filters.orderno = selectBoxes.order.ordno;
      }
    }
    
    // Only include pl_rate_id for AGENTBILL document type
    if (selectBoxes.pricelist?.rateid && docType === 'AGENTBILL') {
      filters.pl_rate_id = selectBoxes.pricelist?.rateid;
    }
    
    return filters;
  }, [selectBoxes.order, selectBoxes.pricelist?.rateid, docType]);

  const {
    data: { data: allOrderDetails } = [],
    isFetching: isFetchingOrderDetails,
  } = useAllAgOrderDetails(
    orderDetailsFilters, 
    { enabled: Boolean(selectBoxes.order && (Array.isArray(selectBoxes.order) ? selectBoxes.order.length > 0 : selectBoxes.order.ordno)) }
  );

  // Packing list details fetching hook
  const {
    data: packingListDetails = [],
    isFetching: isFetchingPackingListDetails,
  } = usePackingListDetails(
    {
      packingListIds: Array.isArray(selectBoxes.packingList) 
        ? selectBoxes.packingList.map(pl => pl.salebillid || pl).join(',')
        : selectBoxes.packingList?.salebillid || selectBoxes.packingList,
      vendorId: selectBoxes.vendor?.ps_Id,
      effectiveDate: selectBoxes.pricelist?.effrctivedt || '',
      traderBillType: traderBillType,
    },
    {
      enabled: Boolean(selectBoxes.packingList?.length > 0 && selectBoxes.vendor?.ps_Id && selectBoxes.pricelist?.effrctivedt),
    }
  );

  // Agent and trader purchase: vendor profile. Trader sale: customer profile.
  const profilingPartyId = useMemo(() => {
    if (agttp === 1 && traderBillType === "S") {
      return selectBoxes.customer?.ps_Id || "";
    }
    return selectBoxes.vendor?.ps_Id || "";
  }, [
    agttp,
    traderBillType,
    selectBoxes.customer?.ps_Id,
    selectBoxes.vendor?.ps_Id,
  ]);

  const {
    data: profilingMaster,
    isLoading: isLoadingProfiling,
  } = useProfilingMasterByParty(
    profilingPartyId,
    {
      enabled:
        (docType === "AGENTBILL" || docType === "CNDN") && !!profilingPartyId,
      refetchOnWindowFocus: false,
    }
  );

  // PHP setvendprof: due date = bill date + profile payment days. Skip if no profile.
  useEffect(() => {
    if (editBillId || !profilingMaster || !billHeader.salebilldate) return;

    const paymentDays = parseInt(profilingMaster.noofdayspay, 10) || 0;
    const dueDate = new Date(`${billHeader.salebilldate}T00:00:00`);
    if (Number.isNaN(dueDate.getTime())) return;

    dueDate.setDate(dueDate.getDate() + paymentDays);
    const year = dueDate.getFullYear();
    const month = String(dueDate.getMonth() + 1).padStart(2, "0");
    const day = String(dueDate.getDate()).padStart(2, "0");
    const nextDueDate = `${year}-${month}-${day}`;

    if (billHeader.salebillduedate !== nextDueDate) {
      updateBillHeader({ salebillduedate: nextDueDate });
    }
  }, [
    editBillId,
    profilingMaster,
    billHeader.salebilldate,
    billHeader.salebillduedate,
    updateBillHeader,
  ]);

  // Helper function to get default date within financial year (timezone-aware)
  const getDefaultDate = useCallback((fys, fye) => {
    const today = new Date();
    const startDate = new Date(fys);
    const endDate = new Date(fye);

    if (today >= startDate && today <= endDate) {
      // Use local date instead of UTC to avoid timezone issues
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } else if (today < startDate) {
      return fys; // Start of FY
    } else {
      return fye; // End of FY
    }
  }, []);

  // Initialize default date if not set
  useMemo(() => {
    if (!billHeader.salebilldate && fys && fye) {
      updateBillHeader({ salebilldate: getDefaultDate(fys, fye) });
    }
  }, [billHeader.salebilldate, fys, fye, updateBillHeader, getDefaultDate]);

  // Document type options based on docType
  const getDocumentTypeOptions = useCallback((docType) => {
    if (docType === "CNDN") {
      return [
        { value: "VCN", label: "Credit Note" },
        { value: "VDN", label: "Debit Note" },
      ];
    }

    const allOptions = [
      { value: "PL", label: "Packing List" },
      { value: "ORD", label: "Order" },
      { value: "DIR", label: "Direct" },
      { value: "STK", label: "Stock" },
    ];

    return docType === "PL"
      ? allOptions.filter((r) => r.value !== "PL")
      : allOptions;
  }, []);

  // Input change handler
  const handleInputChange = useCallback(
    (key, value) => {
      updateBillHeader({ [key]: value });
    },
    [updateBillHeader]
  );

  // Date change handler with financial year validation
  const handleDateChange = useCallback(
    (e) => {
      let selectedDate;
      const newDate = new Date(e.target.value);
      const startDate = new Date(fys);
      const endDate = new Date(fye);

      if (newDate >= startDate && newDate <= endDate) {
        selectedDate = e.target.value;
      } else if (newDate < startDate) {
        selectedDate = fys;
      } else {
        selectedDate = fye;
      }

      updateBillHeader({ salebilldate: selectedDate });
    },
    [fys, fye, updateBillHeader]
  );

  // Vendor change handler
  const handleVendorChange = useCallback(
    (val) => {
      updateSelectBox("vendor", val);
      updateSelectBox("pricelist", null);
      updateSelectBox("order", null);
      setPricelistFilters({ supid: val?.ps_Id || "" });
      updateBillHeader({ vendid: val?.ps_Id || "" });
    },
    [updateSelectBox, setPricelistFilters, updateBillHeader]
  );

  // Customer change handler
  const handleCustomerChange = useCallback(
    (val) => {
      updateSelectBox("customer", val);
      updateSelectBox("order", null);
      const isForeignCustomer =
        Boolean(val?.ps_country) && val.ps_country.toUpperCase() !== "IN";
      updateBillHeader({
        cusid: val?.ps_Id || "",
        currency_code: isForeignCustomer ? "USD" : "INR",
      });
    },
    [updateSelectBox, updateBillHeader]
  );

  // Transporter change handler
  const handleTransporterChange = useCallback(
    (val) => {
      updateSelectBox("transporter", val);
      updateBillHeader({ tptid: val?.ps_Id || "" });
    },
    [updateSelectBox, updateBillHeader]
  );

  // Bill type change handler
  const handleBillTypeChange = useCallback(
    (val) => {
      updateSelectBox("billType", val);
      updateBillHeader({ billtype: val?.value || null });
      
      // Reset order selection and table data when bill type changes
      updateSelectBox("order", null);
      setTableData([]);
      setShowTables(false);
    },
    [updateSelectBox, updateBillHeader, setTableData, setShowTables]
  );

  // Pricelist change handler
  const handlePricelistChange = useCallback(
    (val) => {
      updateSelectBox("pricelist", val);
      updateBillHeader({ pldate: val?.effrctivedt || "" });
    },
    [updateSelectBox, updateBillHeader]
  );

  // Order change handler
  const handleOrderChange = useCallback(
    (val) => {
      updateSelectBox("order", val);
    },
    [updateSelectBox]
  );

  // Packing list change handler
  const handlePackingListChange = useCallback(
    (val) => {
      updateSelectBox("packingList", val);
    },
    [updateSelectBox]
  );

  // Check if top table is filled
  const checkIfTopTableFilled = useCallback(
    (currentDocType) => {
      const hasRequiredParty =
        agttp === 0
          ? Boolean(selectBoxes.customer && selectBoxes.vendor)
          : traderBillType === "S"
            ? billHeader.settlementMode === "CASH" || Boolean(selectBoxes.customer)
            : Boolean(selectBoxes.vendor);

      const hasAutomaticBillNumber =
        currentDocType === "AGENTBILL" &&
        agttp === 1 &&
        traderBillType === "S" &&
        isTraderSalebillAutoNumberEnabled();
      const hasRequiredGodown =
        agttp !== 1 ||
        !["AGENTBILL", "CNDN"].includes(currentDocType) ||
        Number(billHeader.godown) > 0;
      const hasBaseHeader = Boolean(
        (billHeader.salebillno || hasAutomaticBillNumber) &&
        billHeader.salebilldate &&
        selectBoxes.billType &&
        hasRequiredParty &&
        hasRequiredGodown
      );

      return currentDocType === "PL" || currentDocType === "CNDN"
        ? hasBaseHeader
        : hasBaseHeader && (agttp === 1 || Boolean(selectBoxes.pricelist));
    },
    [
      agttp,
      billHeader.godown,
      billHeader.salebilldate,
      billHeader.salebillno,
      billHeader.settlementMode,
      selectBoxes,
      traderBillType,
      isTraderSalebillAutoNumberEnabled,
    ]
  );

  // Handle show tables
  const handleShowTables = useCallback(
    (docType) => {
      if (
        !checkIfTopTableFilled(docType) ||
        (selectBoxes.billType?.value === "ORD" && !selectBoxes.order) ||
        (selectBoxes.billType?.value === "PL" && !selectBoxes.packingList)
      ) {
        return false; // Return false to indicate validation failed
      }

      // For Order type, data will be fetched automatically by the hook
      // when selectBoxes.order.ordno is available
      if (selectBoxes.billType?.value === "ORD" && selectBoxes.order) {
        // Data fetching is handled automatically by useAllAgOrderDetails hook
      }
      // For Packing List type
      else if (selectBoxes.billType?.value === "PL" && selectBoxes.packingList) {
        // Data fetching is handled automatically by usePackingListDetails hook
      }

      setShowTables(true);
      return true; // Return true to indicate validation passed
    },
    [
      checkIfTopTableFilled,
      selectBoxes.billType,
      selectBoxes.order,
      selectBoxes.packingList,
      setShowTables,
    ]
  );

  // Update item quantity handler
  const onUpdateItemQty = useCallback(
    (itemsToAdd) => {
      // itemsToAdd from DirStkTable is already in the final tableData format.
      // So, we just need to add them.
      addItemsToTable(itemsToAdd);
    },
    [addItemsToTable]
  );

  // Remove table item handler
  const removeTableItemsRow = useCallback(
    (index) => {
      removeTableItem(index);
    },
    [removeTableItem]
  );

  const isLoadingTableData = isFetchingOrderDetails || isFetchingPackingListDetails;

  // A company without a GST number must never retain tax data in editable rows,
  // including rows restored from an older bill or loaded from order/packing data.
  useEffect(() => {
    if (hasApplicableGst || !tableData.some((row) =>
      Number(row.tax) !== 0 || Number(row.srate_incl) !== Number(row.srate)
    )) {
      return;
    }

    setTableData(tableData.map((row) => ({
      ...row,
      tax: 0,
      srate_incl: Number(row.srate) || 0,
    })));
  }, [hasApplicableGst, setTableData, tableData]);

  // Computed properties
  const totalGross = useMemo(() => {
    if (docType === "PL") {
      return 0;
    }

    // CNDN Direct(Without Item): use grossamt from billHeader
    if (docType === "CNDN" && billHeader.cndntype === "Direct(Without Item)") {
      return roundAmount2Dig(parseFloat(billHeader.grossamt) || 0);
    }
    
    const isCndn = docType === "CNDN";
    const total = tableData.reduce((gross, x) => {
      if (x.selected) {
        if (isCndn) {
          gross += Number(x.srate) * Number(x.sqty);
        } else {
          const netRate = getItemNetRate(x);
          if (netRate >= 0) {
            gross += netRate * Number(x.sqty) * Number(x.slotqty);
          }
        }
      }
      return gross;
    }, 0);

    return roundAmount2Dig(total);
  }, [tableData, docType, billHeader.cndntype, billHeader.grossamt]);

  const totalTax = useMemo(() => {
    // Skip tax calculations for PL (Packing List/Shipping Info) document type
    if (docType === "PL" || !hasApplicableGst) {
      return 0;
    }
    
    // Calculate discount ratio from bill-level discounts (matching PHP logic)
    // dis_ratio = dis_sum / gross
    let discountSum = 0;
    if (beforeTaxDiscounts && beforeTaxDiscounts.length > 0) {
      beforeTaxDiscounts.forEach(discount => {
        if (discount.is_addition === 1) {
          discountSum -= Number(discount.amount) || 0;
        } else {
          discountSum += Number(discount.amount) || 0;
        }
      });
    }
    
    const discountRatio = totalGross > 0 ? discountSum / totalGross : 0;
    
    const isCndn = docType === "CNDN";
    const total = tableData.reduce((tax, x) => {
      if (x.selected) {
        let rowAmount;
        if (isCndn) {
          rowAmount = Number(x.srate) * Number(x.sqty);
        } else {
          const netRate = getItemNetRate(x);
          if (netRate < 0) return tax;
          rowAmount = netRate * Number(x.sqty) * Number(x.slotqty);
        }
        const rowAmountAfterDiscount = rowAmount * (1 - discountRatio);
        const rowTax = (rowAmountAfterDiscount * x.tax) / 100;
        tax += rowTax;
      }
      return tax;
    }, 0);

    return roundAmount2Dig(total);
  }, [tableData, docType, beforeTaxDiscounts, totalGross, hasApplicableGst]);

  const totalQty = useMemo(() => {
    const total = tableData.reduce((qty, x) => {
      if (x.selected) {
        qty += Number(x.sqty) * Number(x.slotqty) || 0;
      }
      return qty;
    }, 0);

    return total;
  }, [tableData]);

  const totalQtyPcs = useMemo(() => {
    const total = tableData.reduce((qty, x) => {
      if (x.selected) {
        qty += Number(x.sqty) * Number(x.item.aguomconval) * Number(x.slotqty) || 0;
      }
      return qty;
    }, 0);

    return total;
  }, [tableData]);

  // IGST vs CGST+SGST — mirrors PHP get_type_of_tax / vendor_typeoftax*
  // Agent: vendor state vs customer state
  // Trader sale/purchase: counterparty state vs company state
  // Trader cash sale: always CGST+SGST ("S")
  const isIGST = useMemo(() => {
    const toState = (value) => {
      const n = Number(value);
      return Number.isFinite(n) && n > 0 ? n : null;
    };

    if (agttp === 1) {
      if (traderBillType === "S" && billHeader.settlementMode === "CASH") {
        return false;
      }

      const partyState = toState(
        traderBillType === "P"
          ? selectBoxes.vendor?.stateid
          : selectBoxes.customer?.stateid
      );
      const companyState = toState(companyStateId);

      if (!partyState || !companyState) {
        return true;
      }

      return partyState !== companyState;
    }

    const customerState = toState(selectBoxes.customer?.stateid);
    const vendorState = toState(selectBoxes.vendor?.stateid);

    if (!customerState || !vendorState) {
      return true;
    }

    return customerState !== vendorState;
  }, [
    agttp,
    traderBillType,
    billHeader.settlementMode,
    selectBoxes.customer,
    selectBoxes.vendor,
    companyStateId,
  ]);

  // Filtered table data - separate regular items from addon items
  const regularTableData = useMemo(() => {
    return tableData.filter(item => !item.isAdditionalItems);
  }, [tableData]);

  const addonTableData = useMemo(() => {
    return tableData.filter(item => item.isAdditionalItems);
  }, [tableData]);

  // Group selected items by order number and case number (only from regular items)
  const orderCasesGrouped = useMemo(() => {
    const selectedItems = regularTableData.filter(item => item.selected);
    
    const grouped = selectedItems.reduce((acc, item) => {
      const key = `${item.ordno}-${item.caseno}`;
      
      if (!acc[key]) {
        acc[key] = {
          order: item.ordno,
          caseNumber: item.caseno,
          lot: item.slotqty
        };
      }
      
      return acc;
    }, {});
    
    return Object.values(grouped);
  }, [regularTableData]);

  // Process fetched order details and populate table
  useEffect(() => {
    if (allOrderDetails && allOrderDetails.length > 0 && showTables) {
      const processedData = allOrderDetails.map((orderDetail) => ({
        selected: false,
        ordno: orderDetail.orderno || "",
        caseno: orderDetail.caseno || "",
        slotqty: orderDetail.lotqty || 1,
        sqty: orderDetail.qty || 0,
        srate: isForeignCurrencyBill ? 0 : orderDetail.billing_rate || orderDetail.rate || 0,
        srate_incl: hasApplicableGst
          ? exclusiveToInclusive(
              orderDetail.billing_rate || orderDetail.rate || 0,
              orderDetail.tax || 0
            )
          : isForeignCurrencyBill ? 0 : orderDetail.billing_rate || orderDetail.rate || 0,
        dis1: 0,
        dis2perc: 0,
        dis2: 0,
        tax: hasApplicableGst ? orderDetail.tax || 0 : 0,
        item: {
          itemid: orderDetail.itemid || orderDetail.agitemdtlId,
          item_full_name: orderDetail.item_full_name || orderDetail.itemName,
          aguomconval: orderDetail.aguomconval || 1,
          itmhsnId: orderDetail.itmhsnId || "",
          itemuom: orderDetail.itemuom || "",
        },
        source: selectBoxes.billType?.value || "",
        isAdditionalItems: false,
        itemDetails: [],
      }));

      setTableData(processedData);
    }
  }, [allOrderDetails, hasApplicableGst, isForeignCurrencyBill, showTables, setTableData, selectBoxes.billType?.value]);

  // Process fetched packing list details and populate table
  // Only run when packing list data actually changes, not on every render
  useEffect(() => {
    if (packingListDetails && packingListDetails.length > 0 && showTables && selectBoxes.billType?.value === "PL") {
      // Only update if tableData is empty or different length (new data loaded)
      if (tableData.length === 0 || tableData.length !== packingListDetails.length) {
        const processedData = packingListDetails.map((detail) => ({
          selected: true, // PL items are always selected by default
          ordno: detail.ordno || "",
          caseno: detail.caseno || "",
          challan_no: detail.challan_no || detail.salebill_no || "", // Add challan number for PL
          packingno: detail.salebill_id || detail.packingno || "", // Add packing list ID for tracking
          slotqty: detail.slotqty || 1,
          sqty: detail.sqty || 0,
          srate: isForeignCurrencyBill ? 0 : detail.calculated_rate || 0,
          srate_incl: hasApplicableGst
            ? exclusiveToInclusive(detail.calculated_rate || 0, detail.tax_rate || 0)
            : isForeignCurrencyBill ? 0 : detail.calculated_rate || 0,
          dis1: 0,
          dis2perc: 0,
          dis2: 0,
          tax: hasApplicableGst ? detail.tax_rate || 0 : 0,
          item: {
            itemid: detail.itemid,
            item_full_name: detail.item_full_name,
            aguomconval: detail.aguomconval || 1,
            itmhsnId: detail.itmhsnId || "",
            itemuom: detail.itemuom || "",
          },
          source: "PL",
          isAdditionalItems: false,
          itemDetails: [],
        }));

        setTableData(processedData);
      }
    }
  }, [packingListDetails, hasApplicableGst, isForeignCurrencyBill, showTables, setTableData, selectBoxes.billType?.value, tableData.length]);

  // Update beforeTaxDiscounts when profiling master changes
  // Only include before-tax discounts (B), not annual (A) or after-tax (C)
  // In edit mode: merge saved bill discounts with current profile discounts
  useEffect(() => {
    if ((docType === "AGENTBILL" || docType === "CNDN") && profilingMaster?.discountDetails) {
      const profileDiscounts = profilingMaster.discountDetails
        .filter((discount) => discount.discount_type === 'B')
        .map((discount) => ({
          id: discount.id,
          name: discount.discount.name,
          shortname: discount.discount.shortname,
          order: discount.discount_order,
          type: discount.discount_type,
          percentage: parseFloat(discount.percentage_value) || 0,
          amount: parseFloat(discount.amount_value) || 0,
          discountId: discount.discount_id,
          is_addition: discount.discount?.is_addition || 0,
        }))
        .sort((a, b) => a.order - b.order);

      if (editBillId && savedBillDiscounts.length > 0) {
        // Edit mode: merge saved bill discounts with current profile
        // 1. Start with saved bill discounts (they have actual bill amounts)
        // 2. Add any new profile discounts not in the saved bill
        const mergedMap = new Map();

        // Add saved bill discounts first (with their saved amounts)
        savedBillDiscounts.forEach((saved) => {
          mergedMap.set(saved.discountId, { ...saved });
        });

        // Add profile discounts that aren't already in saved bill
        profileDiscounts.forEach((profile) => {
          if (!mergedMap.has(profile.discountId)) {
            mergedMap.set(profile.discountId, {
              ...profile,
              percentage: profile.percentage || 0,
              amount: profile.amount || 0,
            });
          } else {
            // For existing ones, keep saved amounts but update name/shortname from profile
            const existing = mergedMap.get(profile.discountId);
            mergedMap.set(profile.discountId, {
              ...existing,
              name: profile.name || existing.name,
              shortname: profile.shortname || existing.shortname,
              type: profile.type || existing.type,
              is_addition: profile.is_addition ?? existing.is_addition ?? 0,
            });
          }
        });

        const merged = Array.from(mergedMap.values())
          .sort((a, b) => a.order - b.order);
        setBeforeTaxDiscounts(merged);
      } else {
        // Create mode: use profile discounts directly
        setBeforeTaxDiscounts(profileDiscounts);
      }
    } else if (!editBillId) {
      // Only reset for create mode; don't clear saved discounts during edit
      setBeforeTaxDiscounts([]);
    }
  }, [docType, profilingMaster, setBeforeTaxDiscounts, editBillId, savedBillDiscounts]);

  // Update afterTaxDiscounts when profiling master changes (C type)
  useEffect(() => {
    if ((docType === "AGENTBILL" || docType === "CNDN") && profilingMaster?.discountDetails) {
      const profileDiscounts = profilingMaster.discountDetails
        .filter((discount) => discount.discount_type === 'C')
        .map((discount) => ({
          id: discount.id,
          name: discount.discount.name,
          shortname: discount.discount.shortname,
          order: discount.discount_order,
          type: discount.discount_type,
          percentage: parseFloat(discount.percentage_value) || 0,
          amount: parseFloat(discount.amount_value) || 0,
          discountId: discount.discount_id,
          is_addition: discount.discount?.is_addition || 0,
        }))
        .sort((a, b) => a.order - b.order);

      if (editBillId && savedAfterTaxDiscounts.length > 0) {
        const mergedMap = new Map();
        savedAfterTaxDiscounts.forEach((saved) => {
          mergedMap.set(saved.discountId, { ...saved });
        });
        profileDiscounts.forEach((profile) => {
          if (!mergedMap.has(profile.discountId)) {
            mergedMap.set(profile.discountId, {
              ...profile,
              percentage: profile.percentage || 0,
              amount: profile.amount || 0,
            });
          } else {
            const existing = mergedMap.get(profile.discountId);
            mergedMap.set(profile.discountId, {
              ...existing,
              name: profile.name || existing.name,
              shortname: profile.shortname || existing.shortname,
              type: profile.type || existing.type,
              is_addition: profile.is_addition ?? existing.is_addition ?? 0,
            });
          }
        });
        const merged = Array.from(mergedMap.values())
          .sort((a, b) => a.order - b.order);
        setAfterTaxDiscounts(merged);
      } else {
        setAfterTaxDiscounts(profileDiscounts);
      }
    } else if (!editBillId) {
      setAfterTaxDiscounts([]);
    }
  }, [docType, profilingMaster, setAfterTaxDiscounts, editBillId, savedAfterTaxDiscounts]);

  // Scheme issue prefill from query params (e.g. /salebill/new?fromSchemeIssue=true&...)
  const schemePrefillApplied = useRef(false);

  useEffect(() => {
    if (schemePrefillApplied.current) return;
    if (searchParams.get("fromSchemeIssue") !== "true") return;
    schemePrefillApplied.current = true;

    const billNo = searchParams.get("billNo");
    const remark = searchParams.get("remark");
    const date = searchParams.get("date");
    const customerId = searchParams.get("customerId");
    const customerName = searchParams.get("customerName");
    const vendorId = searchParams.get("vendorId");
    const vendorName = searchParams.get("vendorName");
    const totalQty = parseFloat(searchParams.get("totalQty")) || 0;
    const totalAmt = parseFloat(searchParams.get("totalAmt")) || 0;
    const schemeItemId = searchParams.get("schemeItemId");
    const schemeItemName = searchParams.get("schemeItemName");
    const schemeItemUom = searchParams.get("schemeItemUom");
    const schemeItemHsn = parseInt(searchParams.get("schemeItemHsn")) || 0;
    const schemeItemConval = parseFloat(searchParams.get("schemeItemConval")) || 1;

    handleBillTypeChange({ value: "DIR", label: "Direct" });

    if (customerId) {
      handleCustomerChange({ ps_Id: customerId, name: customerName || customerId });
    }

    if (vendorId) {
      handleVendorChange({ ps_Id: vendorId, name: vendorName || vendorId });
    }

    updateBillHeader({
      ...(billNo ? { salebillno: billNo } : {}),
      ...(date ? { salebilldate: date } : {}),
      ...(remark ? { remark } : {}),
      is_scheme: 1,
    });

    if (schemeItemId) {
      const srate = totalAmt > 0 && totalQty > 0 ? totalAmt / totalQty : 0;
      setTableData([
        {
          selected: true,
          ordno: "DIR",
          caseno: 1,
          slotqty: 1,
          sqty: totalQty,
          srate,
          dis1: 0,
          dis2perc: 0,
          dis2: 0,
          tax: 0,
          packingno: "",
          item: {
            itemid: schemeItemId,
            item_full_name: schemeItemName || "Scheme Item",
            itemName: "Scheme Item",
            itemCode: "-",
            brand: "-",
            subbrandname: "",
            itemuom: schemeItemUom || "-",
            itmhsnId: schemeItemHsn,
            aguomconval: schemeItemConval,
            shp: "-",
            clt: "-",
            sz: "-",
            szuom: "-",
          },
          source: "DIR",
          isAdditionalItems: false,
          itemDetails: [],
        },
      ]);
      setShowTables(true);
    }
  }, [searchParams, handleBillTypeChange, handleCustomerChange, handleVendorChange, updateBillHeader, setTableData, setShowTables]);

  return {
    // State
    billHeader,
    docType,
    selectBoxes,
    tableData,
    regularTableData,
    addonTableData,
    showTables,
    pricelistFilters,
    queryPricelist,
    beforeTaxDiscounts,
    afterTaxDiscounts,
    agentCommission,
    billMode,
    setBillMode,

    // Actions
    setBillHeader,
    updateBillHeader,
    resetBillHeader,
    resetStore,
    setDocType,
    updateSelectBox,
    setSelectBoxes,
    resetSelectBoxes,
    setTableData,
    setShowTables,
    addItemsToTable,
    removeTableItem,
    updateTableItem,
    setPricelistFilters,
    setQueryPricelist,
    setBeforeTaxDiscounts,
    updateBeforeTaxDiscount,
    setAfterTaxDiscounts,
    updateAfterTaxDiscount,
    setAgentCommission,
    updateAgentCommission,
    populateBillFromData,

    // Handlers
    handleInputChange,
    handleDateChange,
    handleVendorChange,
    handleCustomerChange,
    handleTransporterChange,
    handleBillTypeChange,
    handlePricelistChange,
    handleOrderChange,
    handlePackingListChange,
    handleShowTables,
    onUpdateItemQty,
    removeTableItemsRow,

    // Scheme issue flag — true when redirected from scheme issue OR when editing a scheme bill
    isFromSchemeIssue: searchParams.get("fromSchemeIssue") === "true" || billHeader.is_scheme === 1,

    // Computed properties
    totalGross,
    totalTax,
    companyHasGst,
    hasApplicableGst,
    isForeignCounterparty,
    isForeignCurrencyBill,
    totalQty,
    totalQtyPcs,
    isIGST,
    orderCasesGrouped,


    // Tax breakdown (for display and API)
    taxBreakdown: useMemo(() => ({
      cgstamt: hasApplicableGst && !isIGST ? totalTax / 2 : 0,
      sgstamt: hasApplicableGst && !isIGST ? totalTax / 2 : 0,
      igstamt: hasApplicableGst && isIGST ? totalTax : 0,
    }), [hasApplicableGst, isIGST, totalTax]),

    // Profiling master data
    profilingMaster,
    isLoadingProfiling,

    // Trader bill type
    traderBillType,
    setTraderBillType,

    // Case diversion state and actions
    confirmpltransfer,
    setCaseDiversion,
    clearCaseDiversion,
    handleCaseDiversionConfirm,

    // Modal states
    showCaseDiversionModal,
    setShowCaseDiversionModal,
    resetCaseDiversionModal,

    // Table data loading state
    isLoadingTableData,

    // Utilities
    isHeaderComplete,
    checkIfTopTableFilled,
    getDocumentTypeOptions,
    getDefaultDate,

    // Mutation functions
    createShippingInfo,
    updateShippingInfo,
    isCreatingShippingInfo,
    isUpdatingShippingInfo,
    createSaleBill,
    isCreatingSaleBill,
    updateSaleBill,
    isUpdatingSaleBill,
    checkSalebillDuplicate,
    populateShippingInfoForEdit: useCallback((shippingInfoData) => {
      // Populate bill header and selectBoxes
      const billTypeValue = shippingInfoData.billtype || "";
      const billTypeOption = billTypeValue ? { 
        value: billTypeValue, 
        label: billTypeValue === "ORD" ? "Order" : billTypeValue === "PL" ? "Packing List" : billTypeValue === "DIR" ? "Direct" : billTypeValue === "STK" ? "Stock" : billTypeValue
      } : null;

      updateBillHeader({
        salebillno: shippingInfoData.salebillno || "",
        salebilldate: shippingInfoData.salebilldate || "",
        orddespatch: shippingInfoData.orddespatch || "",
        orderedby: shippingInfoData.orderedby || "",
        lrno: shippingInfoData.lrno || "",
        lr_date: shippingInfoData.lr_date || null,
        salebillduedate: shippingInfoData.salebillduedate || null,
        billtype: billTypeValue,
        remark: shippingInfoData.remark || "",
        cusid: shippingInfoData.customer?.ps_Id || "",
        vendid: shippingInfoData.vendor?.ps_Id || "",
        tptid: shippingInfoData.transport?.ps_Id || "",
      });

      handleCustomerChange(shippingInfoData.customer ? {
        ps_Id: shippingInfoData.customer.ps_Id,
        name: shippingInfoData.customer.name,
      } : null);

      handleVendorChange(shippingInfoData.vendor ? {
        ps_Id: shippingInfoData.vendor.ps_Id,
        name: shippingInfoData.vendor.name,
      } : null);

      handleTransporterChange(shippingInfoData.transport ? {
        ps_Id: shippingInfoData.transport.ps_Id,
        name: shippingInfoData.transport.name,
      } : null);

      // Set bill type directly without triggering the reset side effects of handleBillTypeChange
      updateSelectBox("billType", billTypeOption);

      // Populate table data if details exist
      if (shippingInfoData.details && Array.isArray(shippingInfoData.details) && shippingInfoData.details.length > 0) {
        const formattedTableData = shippingInfoData.details.map(detail => {
          const itemDetail = detail.salebill_itemdetail;
          const itemMast = itemDetail?.agitemmast;

          return {
            selected: true,
            caseno: detail.caseno,
            ordno: detail.ordno || "",
            slotqty: detail.slotqty || 0,
            sqty: detail.sqty || 0,
            itemDetails: detail.itemDetails || [],
            item: {
              itemid: detail.itemid,
              item_full_name: detail.item_full_name || "", // Use the full item name from backend
              itemName: itemMast?.itemName || "",
              itemCode: itemMast?.itemCode || "",
              brand: itemMast?.brand || "",
              subbrandname: itemMast?.subbrandname || "",
              itemuom: itemMast?.itemuom || "",
              itmhsnId: detail.itmhsnid,
              aguomconval: itemMast?.aguomconval || 1,
              shp: itemDetail?.shp || "",
              clt: itemDetail?.clt || "",
              sz: itemDetail?.sz || "",
              szuom: itemDetail?.szuom || "",
            },
            source: billTypeValue,
            isAdditionalItems: false,
          };
        });

        setTableData(formattedTableData);
        // Set showTables to true so the tables are visible
        setShowTables(true);
      }
    }, [updateBillHeader, handleCustomerChange, handleVendorChange, handleTransporterChange, updateSelectBox, setTableData, setShowTables]),
  };
};
